import React, { createContext, useReducer, useContext } from 'react';
import { getSplitCityStateZip } from './util/getSplitCityStateZip';
import { sanitizeAddressInput } from './util/validationHelpers';
export const initialState = {
    requestValueWhere: '',
    requestValueSplitStreet: '',
    requestValueSplitCityStateZip: '',
    selectedItem: null,
    prevSelectedItem: null,
    inputValid: true,
    errorCode: undefined,
    errorMessageSplitStreet: undefined,
    errorMessageSplitCityStateZip: undefined,
    userInput: '',
    isEscapeHatch: false,
};
/**
 * Reduces actions into state updates for the Location Input Component.
 * This reducer handles the state transitions based on the dispatched actions, updating the component's state accordingly.
 *
 * Actions handled:
 * - `UPDATE_INPUT`: Updates the state with new input values, checking validity, and setting the selected item if provided.
 *   This action can also clear the error message if the input is deemed valid.
 * - `SET_REQUEST_VALUE`: Sets the current value of the input field directly.
 * - `SET_ERROR_MESSAGE`: Sets an error message and marks the input as invalid.
 * - `CLEAR_ERROR_MESSAGE`: Clears any existing error messages and marks the input as valid.
 * - `CLEAR_STATE`: Resets the state to its initial configuration.
 *
 * @param {LocationInputState} state - The current state of the Location Input Component.
 * @param {LocationInputAction} action - An action to be processed, which may alter the state.
 * @returns {LocationInputState} - The new state after the action has been applied.
 */
const LocationInputComponentReducer = (state, action) => {
    var _a, _b, _c, _d, _e, _f;
    switch (action.type) {
        case 'UPDATE_INPUT':
            const { value, isValid, selectedItem, userInput, isEscapeHatch } = action.payload;
            const prevSelectedItem = state.selectedItem ? { ...state.selectedItem } : null;
            return {
                ...state,
                requestValueWhere: value,
                selectedItem: { ...(selectedItem || state.selectedItem), isValid }, // Use existing if not provided
                prevSelectedItem: prevSelectedItem,
                inputValid: isValid,
                ...(selectedItem
                    ? {
                        ...(((_a = selectedItem.location) === null || _a === void 0 ? void 0 : _a.getPreciseLocationCode())
                            ? {
                                requestValueSplitStreet: ((_b = selectedItem.location) === null || _b === void 0 ? void 0 : _b.getPreciseLocationCode()) || '',
                            }
                            : {}),
                        requestValueSplitCityStateZip: getSplitCityStateZip(selectedItem),
                    }
                    : {}),
                userInput: userInput,
                isEscapeHatch: isEscapeHatch || false,
            };
        case 'SET_REQUEST_VALUE':
            return {
                ...state,
                requestValueWhere: sanitizeAddressInput(action.payload),
            };
        case 'SET_REQUEST_VALUE_SPLIT_STREET':
            return {
                ...state,
                requestValueSplitStreet: sanitizeAddressInput(action.payload),
            };
        case 'SET_REQUEST_VALUE_SPLIT_ZIP':
            return {
                ...state,
                requestValueSplitCityStateZip: sanitizeAddressInput(action.payload),
            };
        case 'SET_ERROR_MESSAGE':
            if (state.errorCode === action.payload.errorCode &&
                !state.inputValid &&
                !((_c = state.selectedItem) === null || _c === void 0 ? void 0 : _c.isValid))
                return state;
            return {
                ...state,
                selectedItem: {
                    ...state.selectedItem,
                    isValid: false,
                },
                inputValid: false,
                errorCode: action.payload.errorCode,
                userInput: action.payload.userInput,
            };
        case 'SET_ERROR_MESSAGE_SPLIT_STREET':
            if (state.errorCode === action.payload &&
                !state.inputValid &&
                !((_d = state.selectedItem) === null || _d === void 0 ? void 0 : _d.isValid))
                return state;
            return {
                ...state,
                selectedItem: {
                    ...state.selectedItem,
                    isValid: false,
                },
                inputValid: false,
                errorMessageSplitStreet: action.payload,
                errorMessageSplitCityStateZip: undefined,
            };
        case 'SET_ERROR_MESSAGE_SPLIT_ZIP':
            if (state.errorCode === action.payload &&
                !state.inputValid &&
                !((_e = state.selectedItem) === null || _e === void 0 ? void 0 : _e.isValid))
                return state;
            return {
                ...state,
                selectedItem: {
                    ...state.selectedItem,
                    isValid: false,
                },
                inputValid: false,
                errorMessageSplitStreet: undefined,
                errorMessageSplitCityStateZip: action.payload,
            };
        case 'CLEAR_ERROR_MESSAGE':
            if (!state.errorCode && state.inputValid && ((_f = state.selectedItem) === null || _f === void 0 ? void 0 : _f.isValid))
                return state;
            return {
                ...state,
                selectedItem: {
                    ...state.selectedItem,
                    isValid: true,
                },
                inputValid: true,
                errorCode: undefined,
                errorMessageSplitStreet: undefined,
                errorMessageSplitCityStateZip: undefined,
            };
        case 'CLEAR_STATE':
            return {
                ...initialState,
            };
        default:
            return state;
    }
};
const LocationContext = createContext({
    state: initialState,
    dispatch: () => { },
});
export const LocationProvider = ({ children }) => {
    const [state, dispatch] = useReducer(LocationInputComponentReducer, initialState);
    return (React.createElement(LocationContext.Provider, { value: { state, dispatch } }, children));
};
export const useLocationInputContext = () => useContext(LocationContext);
//# sourceMappingURL=data:application/json;base64,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